// Fullscreen lighting pass — direct + voxel cone traced indirect
// Engine code: reads G-buffer + depth + 3D voxel volume, outputs to screen.

import type { GPU } from "./gpu.js";
import type { GBuffer } from "./deferred.js";

const LIGHTING_SHADER = /* wgsl */ `
const MAX_POINT_LIGHTS = 16u;

struct PointLight {
  pos: vec3f,
  radius: f32,
  color: vec3f,
  intensity: f32,
};

struct LightUniforms {
  invViewProj: mat4x4f,
  camPos: vec3f, ambient: f32,
  lightDir: vec3f, giStrength: f32,
  voxelMin: vec3f, reflStrength: f32,
  voxelMax: vec3f, pointLightCount: u32,
  fogColor: vec3f, fogDensity: f32,
  screenSize: vec2f, aoStrength: f32, reflTint: f32,
  lightViewProj: mat4x4f,
  pointLights: array<PointLight, 16>,
};

@group(0) @binding(0) var albedoTex: texture_2d<f32>;
@group(0) @binding(1) var normalTex: texture_2d<f32>;
@group(0) @binding(2) var depthTex: texture_depth_2d;
@group(0) @binding(3) var<uniform> light: LightUniforms;
@group(0) @binding(4) var voxelVolume: texture_3d<f32>;
@group(0) @binding(5) var voxelSampler: sampler;
@group(0) @binding(6) var giTex: texture_2d<f32>;
@group(0) @binding(7) var giSamp: sampler;
@group(0) @binding(8) var shadowMap: texture_depth_2d;
@group(0) @binding(9) var shadowSamp: sampler_comparison;

struct VSOut { @builtin(position) pos: vec4f, @location(0) uv: vec2f };

@vertex fn vsLight(@builtin(vertex_index) vi: u32) -> VSOut {
  let x = f32(i32(vi & 1u) * 4 - 1); let y = f32(i32(vi >> 1u) * 4 - 1);
  var o: VSOut; o.pos = vec4f(x, y, 0.0, 1.0); o.uv = vec2f((x + 1.0) * 0.5, (1.0 - y) * 0.5); return o;
}

// Soft shadow — Poisson disk PCF (16 taps, rotated per-pixel to eliminate banding)
const POISSON = array<vec2f, 16>(
  vec2f(-0.94, -0.34), vec2f( 0.94,  0.26), vec2f(-0.55,  0.86), vec2f( 0.53, -0.85),
  vec2f(-0.19,  0.36), vec2f( 0.33,  0.96), vec2f(-0.97,  0.21), vec2f( 0.61,  0.56),
  vec2f(-0.55, -0.78), vec2f( 0.02, -0.49), vec2f( 0.81, -0.23), vec2f(-0.26, -0.15),
  vec2f( 0.19,  0.23), vec2f(-0.75,  0.52), vec2f( 0.72, -0.62), vec2f(-0.13,  0.72),
);

fn sampleSunShadow(worldPos: vec3f, N: vec3f) -> f32 {
  // Normal offset: push sun-facing surfaces away from shadow map to prevent self-shadowing
  let biasPos = worldPos + N * 0.15;
  let clip = light.lightViewProj * vec4f(biasPos, 1.0);
  let ndc = clip.xyz / clip.w;
  let uv = vec2f(ndc.x * 0.5 + 0.5, 1.0 - (ndc.y * 0.5 + 0.5));

  if (any(uv < vec2f(0.0)) || any(uv > vec2f(1.0)) || ndc.z < 0.0 || ndc.z > 1.0) {
    return 1.0;
  }

  // Per-pixel rotation to break up sampling pattern (no visible banding)
  let angle = fract(sin(dot(worldPos.xz, vec2f(12.9898, 78.233))) * 43758.5453) * 6.2832;
  let cs = cos(angle);
  let sn = sin(angle);

  let radius = 4.0 / 2048.0;
  var shadow = 0.0;
  for (var i = 0; i < 16; i++) {
    let p = POISSON[i];
    let rotated = vec2f(p.x * cs - p.y * sn, p.x * sn + p.y * cs) * radius;
    shadow += textureSampleCompareLevel(shadowMap, shadowSamp, uv + rotated, ndc.z - 0.002);
  }
  return shadow / 16.0;
}

fn reconstructWorldPos(coord: vec2i, depth: f32) -> vec3f {
  let uv = (vec2f(coord) + 0.5) / light.screenSize;
  let ndc = vec4f(uv.x * 2.0 - 1.0, 1.0 - uv.y * 2.0, depth, 1.0);
  let world = light.invViewProj * ndc;
  return world.xyz / world.w;
}

// Convert world position to voxel UV (0..1)
fn worldToVoxelUV(wp: vec3f) -> vec3f {
  return (wp - light.voxelMin) / (light.voxelMax - light.voxelMin);
}

fn buildTBN(N: vec3f) -> mat3x3f {
  var T: vec3f;
  if (abs(N.y) < 0.999) { T = normalize(cross(vec3f(0.0, 1.0, 0.0), N)); }
  else { T = normalize(cross(vec3f(1.0, 0.0, 0.0), N)); }
  let B = cross(N, T);
  return mat3x3f(T, N, B);
}

// 6 cone directions in tangent space (hemisphere)
const CONE_DIRS = array<vec3f, 6>(
  vec3f(0.0, 1.0, 0.0),
  vec3f(0.866, 0.5, 0.0),
  vec3f(-0.866, 0.5, 0.0),
  vec3f(0.0, 0.5, 0.866),
  vec3f(0.0, 0.5, -0.866),
  vec3f(0.0, 0.7071, 0.7071),
);

const CONE_WEIGHTS = array<f32, 6>(
  0.25, 0.15, 0.15, 0.15, 0.15, 0.15,
);

// Point light shadow — inline trace (short distance, sharp)
fn traceShadow(origin: vec3f, dir: vec3f, maxDist: f32) -> f32 {
  let voxelExtent = light.voxelMax - light.voxelMin;
  let voxelSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;

  var vis = 1.0;
  var d = voxelSize * 2.0;

  for (var i = 0; i < 16; i++) {
    if (vis < 0.05 || d > maxDist) { break; }

    let uv = worldToVoxelUV(origin + dir * d);
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }

    let s = textureSampleLevel(voxelVolume, voxelSampler, uv, 0.0);
    if (s.a > 0.9) { vis *= (1.0 - s.a); }
    d += voxelSize;
  }

  return vis;
}

// Trace a single cone through the voxel volume
// Returns vec4: rgb = indirect color, a = occlusion (for AO)
fn traceCone(origin: vec3f, dir: vec3f, coneAngle: f32) -> vec4f {
  var color = vec3f(0.0);
  var occlusion = 0.0;

  let voxelExtent = light.voxelMax - light.voxelMin;
  let voxelSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;
  let maxDist = length(voxelExtent) * 0.5;

  var dist = voxelSize * 4.0; // start offset to avoid self-hit
  let tanAngle = tan(coneAngle);

  for (var i = 0; i < 20; i++) {
    if (occlusion >= 0.95 || dist > maxDist) { break; }

    let samplePos = origin + dir * dist;
    let uv = worldToVoxelUV(samplePos);

    // Out of bounds check
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }

    // Cone diameter determines mip level
    let diameter = 2.0 * tanAngle * dist;
    let mipLevel = log2(max(1.0, diameter / voxelSize));

    let sample = textureSampleLevel(voxelVolume, voxelSampler, uv, mipLevel);

    // Front-to-back compositing
    let a = sample.a * (1.0 - occlusion);
    color += sample.rgb * a;
    occlusion += a;

    // Step size increases with distance (cone widens)
    dist += max(diameter * 0.5, voxelSize);
  }

  return vec4f(color, occlusion);
}

@fragment fn fsLight(in: VSOut) -> @location(0) vec4f {
  let coord = vec2i(in.pos.xy);
  let albedoSample = textureLoad(albedoTex, coord, 0);
  let albedo = albedoSample.rgb;
  let emissive = albedoSample.a;
  let normalSample = textureLoad(normalTex, coord, 0);
  let normal = normalSample.rgb;
  let roughness = normalSample.a;
  if (dot(normal, normal) < 0.01) { return vec4f(light.fogColor, 1.0); }

  // Emissive surfaces: output bright color directly (positive emissive only)
  if (emissive > 0.01) {
    return vec4f(albedo * emissive, 1.0);
  }

  // Negative emissive pixels are handled by a separate composite pass.
  // Output fog color so the opaque scene copy has a clean background.
  if (emissive < -0.05) {
    return vec4f(light.fogColor, 1.0);
  }

  let depth = textureLoad(depthTex, coord, 0);
  let worldPos = reconstructWorldPos(coord, depth);
  let N = normalize(normal);

  // Direct lighting — sun shadow from shadow map (PCF soft)
  let sunIntensity = length(light.lightDir);
  var direct = 0.0;
  if (sunIntensity > 0.001) {
    let L2 = light.lightDir / sunIntensity;
    let ndl = max(dot(N, L2), 0.0);
    let sunShadow = sampleSunShadow(worldPos, N);
    direct = ndl * sunIntensity * sunShadow;
  }

  // Indirect lighting + AO — read from half-res pre-computed GI texture
  let giSample = textureSampleLevel(giTex, giSamp, in.uv, 0.0);
  let indirect = giSample.rgb;
  let ao = giSample.a;

  // Trace origin for reflections (still full-res, only 1 cone)
  var traceOrigin = worldPos + N * 0.3;
  let originUV = worldToVoxelUV(traceOrigin);
  if (all(originUV >= vec3f(0.0)) && all(originUV <= vec3f(1.0))) {
    let originVoxel = textureSampleLevel(voxelVolume, voxelSampler, originUV, 0.0);
    if (originVoxel.a > 0.3) { traceOrigin = worldPos + N * 0.05; }
  }

  // AO affects ambient AND indirect — darkens corners properly
  let aoFactor = 1.0 - clamp(ao, 0.0, 1.0) * light.aoStrength;
  let ambient = light.ambient * aoFactor;

  // Point lights
  var pointContrib = vec3f(0.0);
  for (var p = 0u; p < light.pointLightCount; p++) {
    let pl = light.pointLights[p];
    let toLight = pl.pos - worldPos;
    let dist = length(toLight);
    if (dist > pl.radius) { continue; }
    let Lp = toLight / dist;
    let ndlp = max(dot(N, Lp), 0.0);
    let atten = 1.0 - (dist / pl.radius);
    let falloff = atten * atten;
    let shadow = traceShadow(worldPos + N * 0.1, Lp, dist);
    pointContrib += pl.color * pl.intensity * ndlp * falloff * shadow;
  }

  var lit = albedo * (ambient + direct + pointContrib) + indirect * light.giStrength * aoFactor;

  // Reflections: voxel cone trace
  if (roughness < 0.9) {
    let V = normalize(light.camPos - worldPos);
    let R = reflect(-V, N);

    let reflConeAngle = max(roughness * 0.8, 0.02);
    let reflResult = traceCone(traceOrigin, R, reflConeAngle);
    var reflColor = select(vec3f(0.0), reflResult.rgb, reflResult.a > 0.1);

    // Fresnel (Schlick approximation)
    let cosTheta = max(dot(V, N), 0.0);
    let f0 = 0.04;
    let fresnel = f0 + (1.0 - f0) * pow(1.0 - cosTheta, 5.0);

    // Artistic saturation boost
    let reflLuma = dot(reflColor, vec3f(0.299, 0.587, 0.114));
    let tintedRefl = mix(vec3f(reflLuma), reflColor, light.reflTint);

    let reflAmount = fresnel * (1.0 - roughness) * light.reflStrength;
    lit = mix(lit, tintedRefl, reflAmount);
  }

  // Distance fog
  let dist = length(worldPos - light.camPos);
  let fogFactor = exp(-dist * light.fogDensity);
  lit = mix(light.fogColor, lit, clamp(fogFactor, 0.0, 1.0));

  return vec4f(lit, 1.0);
}
`;

export type PointLightDef = {
  pos: [number, number, number];
  radius: number;
  color: [number, number, number];
  intensity: number;
};

export type LightParams = {
  invViewProj: Float32Array;
  camPos: [number, number, number];
  ambient?: number;
  lightDir: [number, number, number];
  giStrength: number;
  voxelMin: [number, number, number];
  reflStrength?: number;
  voxelMax: [number, number, number];
  fogColor?: [number, number, number];
  fogDensity?: number;
  aoStrength?: number;
  reflTint?: number;
  lightViewProj?: Float32Array;
  pointLights?: PointLightDef[];
};

export class LightingPass {
  private pipeline: GPURenderPipeline;
  private bindGroup: GPUBindGroup;
  private uniformBuffer: GPUBuffer;
  private screenW: number;
  private screenH: number;

  constructor(private gpu: GPU, gBuffer: GBuffer, depthTexture: GPUTexture, voxelVolume: GPUTexture, voxelSampler: GPUSampler, giTexture: GPUTexture, shadowMapTexture: GPUTexture, targetFormat?: GPUTextureFormat) {
    const { device, format, canvas } = gpu;
    const outputFormat = targetFormat ?? format;
    this.screenW = canvas.width;
    this.screenH = canvas.height;

    const module = device.createShaderModule({ code: LIGHTING_SHADER });
    const layout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "depth" } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 7, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "depth" } },
        { binding: 9, visibility: GPUShaderStage.FRAGMENT, sampler: { type: "comparison" } },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      vertex: { module, entryPoint: "vsLight" },
      fragment: { module, entryPoint: "fsLight", targets: [{ format: outputFormat }] },
      primitive: { topology: "triangle-list" },
    });

    // 224 bytes header (56 floats) + 16 point lights × 32 bytes each = 736 bytes
    this.uniformBuffer = device.createBuffer({ size: 736, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const giSampler = device.createSampler({ minFilter: "linear", magFilter: "linear" });
    const shadowSampler = device.createSampler({ compare: "less", magFilter: "linear", minFilter: "linear" });
    this.bindGroup = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: gBuffer.albedo.createView() },
        { binding: 1, resource: gBuffer.normal.createView() },
        { binding: 2, resource: depthTexture.createView() },
        { binding: 3, resource: { buffer: this.uniformBuffer } },
        { binding: 4, resource: voxelVolume.createView({ dimension: "3d" }) },
        { binding: 5, resource: voxelSampler },
        { binding: 6, resource: giTexture.createView() },
        { binding: 7, resource: giSampler },
        { binding: 8, resource: shadowMapTexture.createView() },
        { binding: 9, resource: shadowSampler },
      ],
    });
  }

  render(encoder: GPUCommandEncoder, params: LightParams, target?: GPUTextureView): void {
    const { device, context } = this.gpu;

    const lights = params.pointLights ?? [];
    const pointCount = Math.min(lights.length, 16);

    const fog = params.fogColor ?? [0.08, 0.08, 0.1];
    const fogDensity = params.fogDensity ?? 0;

    // Header: 56 floats (invViewProj(16) + 6 vec4s(24) + lightViewProj(16))
    const data = new Float32Array(56 + pointCount * 8);
    // invViewProj (16 floats)
    data.set(params.invViewProj, 0);
    // camPos + ambient
    data[16] = params.camPos[0]; data[17] = params.camPos[1]; data[18] = params.camPos[2]; data[19] = params.ambient ?? 0.01;
    // lightDir + giStrength
    data[20] = params.lightDir[0]; data[21] = params.lightDir[1]; data[22] = params.lightDir[2]; data[23] = params.giStrength;
    // voxelMin + reflStrength
    data[24] = params.voxelMin[0]; data[25] = params.voxelMin[1]; data[26] = params.voxelMin[2]; data[27] = params.reflStrength ?? 1.0;
    // voxelMax + pointLightCount
    data[28] = params.voxelMax[0]; data[29] = params.voxelMax[1]; data[30] = params.voxelMax[2];
    const uintView = new Uint32Array(data.buffer);
    uintView[31] = pointCount;
    // fogColor + fogDensity
    data[32] = fog[0]; data[33] = fog[1]; data[34] = fog[2]; data[35] = fogDensity;
    // screenSize + aoStrength + reflTint
    data[36] = this.screenW; data[37] = this.screenH;
    data[38] = params.aoStrength ?? 0.85; data[39] = params.reflTint ?? 1.0;

    // lightViewProj (16 floats at offset 40)
    if (params.lightViewProj) data.set(params.lightViewProj, 40);

    // Point lights (offset 56)
    for (let i = 0; i < pointCount; i++) {
      const pl = lights[i];
      const o = 56 + i * 8;
      data[o] = pl.pos[0]; data[o+1] = pl.pos[1]; data[o+2] = pl.pos[2]; data[o+3] = pl.radius;
      data[o+4] = pl.color[0]; data[o+5] = pl.color[1]; data[o+6] = pl.color[2]; data[o+7] = pl.intensity;
    }

    device.queue.writeBuffer(this.uniformBuffer, 0, data);

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: target ?? context.getCurrentTexture().createView(),
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
        loadOp: "clear", storeOp: "store",
      }],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3);
    pass.end();
  }
}
